import { helpers } from "@vuelidate/validators";
import { get, isNil, set } from "lodash";

export const latitude = helpers.withMessage("El campo debe estar entre -90 y 90", (value: any) => {
  if (isNil(value) || value === "") {
    return true;
  }

  const num = Number(value);

  return !isNaN(num) && num >= -90 && num <= 90;
});

export const longitude = helpers.withMessage("El campo debe estar entre -180 y 180", (value: any) => {
  if (isNil(value) || value === "") {
    return true;
  }

  const num = Number(value);

  return !isNaN(num) && num >= -180 && num <= 180;
});

export const acceptedFileTypes = (allowedTypes: string[]) =>
  helpers.withParams({ type: "acceptedFileTypes", allowedTypes }, (file: File) => {
    const name = get(file, "name", "");

    if (!name) {
      return true;
    }

    const contentType = get(file, "type", "");

    if (allowedTypes.includes("images/*") && contentType.startsWith("image/")) {
      return true;
    }

    return allowedTypes.includes(contentType);
  });

export const maxFileSize = (maxSizeInBytes: number) =>
  helpers.withParams({ type: "maxFileSize", maxSizeInBytes }, (file: File) => {
    const size = get(file, "size");

    if (isNil(size)) {
      return true;
    }

    return size <= maxSizeInBytes;
  });

export const requiredFile = helpers.withParams({ type: "requiredFile" }, (file: File, vm: any) => {
  return !isNil(get(vm, `formData.${vm.path}`, null))
});


export const min = (params: Record<string, any>) => {
  const { thousandSeparator = "," } = params;

  const baseValidator = helpers.withParams(
    { type: "min", thousandSeparator, min: params.min, max: params.max, prefix: null },
    (value: string, _: any, vm: any): boolean => {
      const min: number | null = get(vm, "min", null);

      set(baseValidator, "$params.min", min);
      set(baseValidator, "$params.prefix", get(vm, "prefix", null));

      if (isNil(value) || isNil(min)) {
        return true;
      }

      value = value.replaceAll(thousandSeparator, "");

      return Number(value) >= min;
    }
  );

  return helpers.withMessage((props: any): string => {
    const { min, prefix } = props.$params;

    return prefix ? `El valor debe ser mayor o igual a ${prefix} ${min}` : `El valor debe ser mayor o igual a ${min}`;
  }, baseValidator);
};

export const max = (params: Record<string, any>) => {
  const { thousandSeparator = "," } = params;

  const baseValidator = helpers.withParams(
    { type: "max", thousandSeparator, min: params.min, max: params.max, prefix: null },
    (value: string, _: any, vm: any): boolean => {
      const max: number | null = get(vm, "max", null);

      set(baseValidator, "$params.max", max);
      set(baseValidator, "$params.prefix", get(vm, "prefix", null));

      if (isNil(value) || isNil(max)) {
        return true;
      }

      value = value.replaceAll(thousandSeparator, "");

      return Number(value) <= max;
    }
  );

  return helpers.withMessage((props: any): string => {
    const { max, prefix } = props.$params;

    return prefix ? `El valor debe ser menor o igual a ${prefix} ${max}` : `El valor debe ser menor o igual a ${max}`;
  }, baseValidator);
};

export const range = (params: Record<string, any>) => {
  const { thousandSeparator = "," } = params;

  const baseValidator = helpers.withParams(
    { type: "range", thousandSeparator, min: params.min, max: params.max, prefix: null },
    (value: any, _: any, vm: any): boolean => {
      const min: number | null = get(vm, "min", null);
      const max: number | null = get(vm, "max", null);

      set(baseValidator, "$params.min", min);
      set(baseValidator, "$params.max", max);
      set(baseValidator, "$params.prefix", get(vm, "prefix", null));

      if (isNil(value) || isNil(min) || isNil(max)) {
        return true;
      }

      if (typeof value === "string") {
        value = value.replaceAll(thousandSeparator, "");
      }

      return Number(value) >= Number(min) && Number(value) <= Number(max);
    }
  );

  return helpers.withMessage((props: any): string => {
    const { min, max, prefix } = props.$params;

    return prefix
      ? `El valor debe estar entre ${prefix} ${min} y ${prefix} ${max}`
      : `El valor debe estar entre ${min} y ${max}`;
  }, baseValidator);
};

export const numericString = (params: Record<string, any>) => {
  const { thousandSeparator, decimalSeparator } = params;

  return helpers.withParams(
    { type: "numericString", thousandSeparator, decimalSeparator },
    (value: string) => {
      value = value.replaceAll(thousandSeparator, "") || "";
      const escapedSeparator = decimalSeparator.replaceAll(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
      let regex = new RegExp(`^(0|[1-9][0-9]*)(${escapedSeparator}[0-9]+)?$`, "g");

      return regex.test(value);
    }
  );
};